/**
 * The `records` collection layer: fixed-field declarations, the record-context
 * enricher (`enrichField`), and the mutation helpers (`partitionChanges` /
 * `computeRecordChanges`) plus the relation-change reconcilers.
 */

import { isEqual } from "lodash-es"
import { HEAVYBIT_ORG_RECORD_ID } from "../directory.js"
import {
  type AugerField,
  type EnrichableField,
  formatValue,
  getFieldType,
  hasFieldType,
  isJunctionField,
  isReferenceDef,
  isReferenceField,
  isRelationField,
  isValueField,
  parseLookupPath,
} from "../fields/fields.js"
import { getRelationshipFields } from "../fields/relationships.js"
import {
  type EnrichOptions,
  resolveDirect,
  resolveDirectIds,
  resolveJunction,
  resolveJunctionTargetIds,
  resolveReference,
} from "../fields/resolve.js"
import { type ReferenceFieldType } from "../fields/types.js"
import { isSocialContact } from "../fields/types/reference.js"
import { getSchema, type SchemaField, type SchemaLike } from "../registries/schemas.js"
import { getType } from "../registries/types.js"
import { getUserById } from "../registries/users.js"
import { type BaseItem } from "./item.js"

/** A record as the enricher and the planner accept it, from whatever query read
 *  it: scalar columns, schema values under `data`, plus joined relations at the
 *  top level (vary per query, so `unknown`). The services return each query's own
 *  row type (FullRecord, RecordRow, …), and every one of them satisfies this. */
export type RecordItem = BaseItem & {
  id: number
  type?: string
  schema_id?: number | null
  name?: string | null
  archived_at?: string | null
  data?: Record<string, unknown>
  /** The relation rows a query joined. Absent means "not selected" (the
   *  planner refuses to diff it); `[]` means the record has none. */
  links?: readonly unknown[]
  taggings?: readonly unknown[]
  attachments?: readonly unknown[]
  contacts?: readonly unknown[]
  times?: readonly unknown[]
  sources?: readonly unknown[]
}

/** True if `value` is or contains a resolved entity (a `__typename` object).
 *  Entity fields store ids, so such a blob must never reach a jsonb column. */
function containsResolvedEntity(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(containsResolvedEntity)
  if (value && typeof value === "object") {
    if ("__typename" in value) return true
    return Object.values(value).some(containsResolvedEntity)
  }
  return false
}

// FIXED FIELDS ================================================================

const FIELDS: ReadonlyArray<EnrichableField> = [
  { key: "type", type: "type", label: "Type", isEditable: false, width: 120 },
  { key: "schema_id", type: "schema", label: "Schema", isEditable: true, width: 180 },
  // The owner is a person (the pair's person half): a `user` field stores a
  // person record id, resolved through the staff directory. A write goes
  // through ownerWrite below.
  { key: "owner_person_id", type: "user", label: "Owner", isEditable: true, width: 150 },
  // The org half of the owner pair. Written only with owner_person_id (see
  // ownerWrite), never on its own; not shown.
  {
    key: "owner_org_id",
    type: "number",
    label: "Owner org",
    isEditable: false,
    isHidden: true,
    width: 100,
  },
  { key: "name", type: "string", label: "Name", isEditable: true, width: 250 },
  { key: "slug", type: "string", label: "Slug", isEditable: true, isHidden: true, width: 150 },
  { key: "description", type: "string", label: "Description", isEditable: true, width: 300 },
  { key: "visibility", type: "visibility", label: "Visibility", isEditable: true, width: 100 },
  { key: "created_at", type: "timestamp", label: "Created", isEditable: false, width: 180 },
  { key: "updated_at", type: "timestamp", label: "Updated", isEditable: false, width: 180 },
  { key: "parent_id", type: "parent", label: "Parent", isEditable: true, width: 200 },
]

/** Keys of the editable columns on the `records` table itself; routes changes
 *  top-level vs `data` jsonb in `computeRecordChanges`. */
export const RECORD_FIXED_KEYS: readonly string[] = FIELDS.map((f) => f.key)

/** Concrete fixed-field instances: spread the FieldType def, then the literal so
 *  hand-set props win. */
export const recordFixedFields: AugerField[] = FIELDS.map((f) => {
  const def = getFieldType(f.type)
  const readColumn = (item: unknown) => (item as Record<string, unknown> | null)?.[f.key] ?? null
  // Item-references (e.g. `parent_id` → `parent`) resolve against the record's
  // relation; store-backed fixed fields stay raw. rawValue is always the column.
  const value =
    isReferenceDef(def) && def.relationship
      ? (resolveReference(def, readColumn) ?? readColumn)
      : readColumn
  return { ...def, ...f, value, rawValue: readColumn }
})

// RECORD-CONTEXT ENRICHER =====================================================

/** Relations with an aggregate (schema_key-null) field whose key IS the relation
 *  name. A field keyed here is the aggregate; any other key is a named field. */
export const AGGREGATE_RELATIONS = new Set([
  "taggings",
  "contacts",
  "times",
  "attachments",
  "sources",
])

type ContactRow = { id: number | string; contact_type?: string; label?: string | null }

/** The contact aggregates: one kind of a record's unqualified contacts each, so
 *  a column holds emails or phones, never both. Socials are the url rows whose
 *  `label` names a platform (`isSocialContact`); the rest go by `contact_type`. */
const CONTACT_AGGREGATES: Record<string, (row: ContactRow) => boolean> = {
  emails: (c) => c.contact_type === "email",
  phones: (c) => c.contact_type === "phone",
  locations: (c) => c.contact_type === "location",
  socials: (c) => isSocialContact(c),
}

// LOOKUPS =====================================================================

/** `key` as declared on schema `schemaId`, else on its record type's base
 *  schema — the two halves a record's field set is built from. */
function findDeclaredField(schemaId: number, key: string): SchemaField | null {
  const own = getSchema(schemaId)
  const base = own?.record_type ? getType(own.record_type)?.schema : null
  for (const schema of [own, base]) {
    const field = schema?.fields?.find((f) => f.key === key)
    if (field) return field
  }
  return null
}

/** The field a lookup reads on a linked record of `recordType`: a value field on
 *  the type's base schema, else a value column (`name`, …). The linked record is
 *  selected without its relations, so a relation-typed field has nothing to read. */
function findLookupTarget(recordType: string, key: string): AugerField | null {
  const schema = getType(recordType)?.schema
  const declared = schema?.fields?.find((f) => f.key === key)
  const field = declared
    ? enrichField({ ...declared, schema_id: schema!.id })
    : recordFixedFields.find((f) => f.key === key)
  return field && isValueField(field) ? field : null
}

/**
 * `value` and `format` for a `lookup` field: `path` names a link field on the
 * record and a field on each record it links to (`organization.founded`). The
 * value is that field's value on the one linked record, or a flat array when
 * there are several values (several links, or an array target);
 * `format` renders each through the target field.
 * Null when the path doesn't resolve to a link field and a readable target.
 */
export function resolveLookup(
  field: Pick<AugerField, "path" | "schema_id">
): Pick<AugerField, "value" | "format"> | null {
  const parsed = field.path ? parseLookupPath(field.path) : null
  if (!parsed || field.schema_id == null) return null
  const via = findDeclaredField(field.schema_id, parsed.via)
  if (via?.type !== "link" || !via.recordType) return null
  const target = findLookupTarget(via.recordType, parsed.pick)
  const linked = resolveJunction(getFieldType("link") as ReferenceFieldType, parsed.via)
  if (!target || !linked) return null

  const value = (record: unknown) => {
    // An array target (multi-select) contributes each of its values, so several
    // links never nest: the value is one flat list.
    const values = (linked(record) as unknown[])
      .filter((entity) => entity != null)
      .flatMap((entity) => {
        const v = target.value(entity)
        return Array.isArray(v) ? v : [v]
      })
      .filter((v) => v != null && v !== "")
    return values.length > 1 ? values : (values[0] ?? null)
  }
  const format = (value: unknown) =>
    (Array.isArray(value) ? value : [value])
      .filter((v) => v != null)
      .map((v) => String(formatValue(target, v) ?? ""))
      .filter(Boolean)
      .join(", ")
  return { value, format }
}

/** What the enrichers take: a stored schema field (with its owning schema's
 *  id, once known), or a hand-authored field. */
export type EnrichInput =
  SchemaField | (SchemaField & { schema_id: number }) | EnrichableField | AugerField

/**
 * Record-context enricher: turn a raw schema field into a runtime `Field` for
 * record views, where the item IS the record (value at `record.data[key]`,
 * relations at `record.links` etc.). Spread order is typeDef → schemaField →
 * options; the branches below set `value` / `rawValue` per kind. `listings.ts`
 * carries the parallel `enrichField` for listing items.
 */
export function enrichField(field: EnrichInput, options: EnrichOptions = {}): AugerField {
  // The flat type def carries every inherited prop; `value`/`rawValue` are
  // assigned in every branch before return, so widen the spread and narrow back.
  // Cast kept: the Field is built in steps, and the type system cannot see that
  // every branch below sets `value` and `rawValue` before it returns.
  const def = getFieldType(field.type)
  const merged = { ...def, ...field, ...options } as unknown as AugerField

  // Record columns aren't namespaced, so the data key IS the column key.
  const sourceKey = merged.key as string
  merged.sourceKey = sourceKey

  // schema_key: the field's key within its schema; null for aggregate relations
  // (they span unqualified rows), unset for fixed columns.
  // A schema can declare its own `emails` field, so only an undeclared one is
  // the aggregate. A socials-typed field is always the socials subset.
  const ofKind =
    (merged.schema_id == null ? CONTACT_AGGREGATES[sourceKey] : undefined) ??
    (merged.type === "socials" ? CONTACT_AGGREGATES.socials : undefined)
  const isAggregate = AGGREGATE_RELATIONS.has(sourceKey) || !!ofKind
  if (merged.schema_id != null || isRelationField(merged)) {
    merged.schema_key = isAggregate ? null : sourceKey
  }

  // A contact aggregate (emails, phones, locations, socials): the unqualified
  // contact rows of its kind. Scoped so it doesn't leak the other contact ids
  // the inherited `relationship: "contacts"` would.
  if (ofKind) {
    const owns = (c: ContactRow & { schema_key?: string | null }) =>
      c.schema_key == null && ofKind(c)
    const rows = (item: unknown) =>
      ((item as { contacts?: ContactRow[] } | null)?.contacts ?? []).filter(owns)
    merged.value = rows
    merged.rawValue = (item: unknown) => rows(item).map((c) => c.id)
    // The write-side reconcilers key relation rows by `schema_key` alone, which
    // for an aggregate field (null) would otherwise match every unqualified
    // contact row — every kind, not just this one. Give them the same
    // membership test the read side uses.
    merged.ownsRow = (row) => owns(row as ContactRow)
    return merged
  }

  // Aggregate relations read the whole relation off the record, not `data[key]`:
  // `value` resolves the unqualified rows to entities, `rawValue` their ids. Both
  // scope to `schema_key`-unset rows so named fields don't leak in.
  if (AGGREGATE_RELATIONS.has(sourceKey)) {
    const aggregateField = isReferenceDef(def) ? def : null
    merged.value = (aggregateField && resolveDirect(aggregateField)) ?? merged.value
    merged.rawValue = (aggregateField && resolveDirectIds(aggregateField)) ?? merged.rawValue
    return merged
  }

  // Related: a direct field whose relation the key names (`events`, …) — the
  // type declares none of its own, since each field reads a different one.
  if (merged.type === "related" && isReferenceDef(def)) {
    const relation = { ...def, relationship: sourceKey }
    merged.value = resolveDirect(relation) ?? merged.value
    merged.rawValue = resolveDirectIds(relation) ?? merged.rawValue
    return merged
  }

  // Schema fields default both accessors to `record.data[key]`; the branches
  // below override per kind (and `options` may pre-supply either).
  const readId = (item: unknown) =>
    (item as { data?: Record<string, unknown> } | null)?.data?.[sourceKey] ?? null
  if (typeof merged.rawValue !== "function") {
    merged.rawValue = readId
  }
  if (typeof merged.value !== "function") {
    merged.value = readId
  }

  // Lookup: derived from the records a link field points at; nothing is stored
  // under its own key, so an unresolvable path reads as empty.
  if (merged.type === "lookup") {
    const lookup = resolveLookup(merged)
    merged.value = lookup?.value ?? (() => null)
    merged.rawValue = merged.value
    if (lookup) merged.format = lookup.format
    return merged
  }

  // Schema junction (link/tagging/attachment): both accessors derive from its
  // `schema_key`-scoped rows — `value` hops each to its target entity, `rawValue`
  // is those entities' ids.
  if (isReferenceDef(def) && isJunctionField(merged)) {
    merged.value = resolveJunction(def, sourceKey) ?? merged.value
    merged.rawValue = resolveJunctionTargetIds(def, sourceKey) ?? merged.rawValue
    return merged
  }

  // A reference field. Schema-qualified item relations (contact/time/source)
  // resolve off their `schema_key`-scoped rows, shaped to arity; `rawValue` is
  // those rows' ids. Store-backed refs (user/schema/type) resolve off the raw id.
  if (isReferenceDef(def) && isReferenceField(merged)) {
    const resolved = resolveReference(def, merged.value, sourceKey)
    if (resolved && def.relationship) {
      const rows = (item: unknown) => (resolved(item) as { id: unknown }[]) ?? []
      merged.value = (item) => (merged.isArray ? rows(item) : (rows(item)[0] ?? null))
      merged.rawValue = (item) => {
        const ids = rows(item).map((row) => row.id)
        return merged.isArray ? ids : (ids[0] ?? null)
      }
    } else if (resolved) {
      merged.value = resolved
    }
    return merged
  }

  return merged
}

// MUTATION HELPERS ============================================================

/**
 * Partition a flat changes object between top-level columns (`topLevelKeys`) and a
 * jsonb column, into a Hasura `_set` plus a `hasChanges` flag. Only differing
 * values land; `undefined` changes and `skipKeys` are ignored.
 */
export function partitionChanges(
  current: Record<string, unknown>,
  changes: Record<string, unknown>,
  options: {
    topLevelKeys: ReadonlySet<string>
    jsonbColumn: string
    skipKeys?: ReadonlySet<string>
  }
): { _set: Record<string, unknown>; hasChanges: boolean } {
  const { topLevelKeys, jsonbColumn, skipKeys = new Set() } = options

  // When the jsonb column's own name is ALSO a topLevelKey (listings'
  // `context`; records' `data` never is — it's refused upstream), a changes
  // object can name the column literally (replace/clear) or name keys that
  // merge INTO it — but not both. "Replace the blob AND merge into what it
  // replaced" has no defensible meaning, and any resolution silently discards
  // half the caller's intent in a key-order-dependent way.
  if (
    topLevelKeys.has(jsonbColumn) &&
    jsonbColumn in changes &&
    changes[jsonbColumn] !== undefined
  ) {
    const mergeKeys = Object.keys(changes).filter(
      (k) =>
        k !== jsonbColumn && changes[k] !== undefined && !topLevelKeys.has(k) && !skipKeys.has(k)
    )
    if (mergeKeys.length) {
      throw new Error(
        `partitionChanges: the changes object both replaces "${jsonbColumn}" and ` +
          `merges keys into it (${mergeKeys.join(", ")}) — pass one or the other.`
      )
    }
  }

  const _set: Record<string, unknown> = {}
  let hasChanges = false
  let jsonbChanges: Record<string, unknown> | null = null

  for (const [key, next] of Object.entries(changes)) {
    if (next === undefined) continue
    if (skipKeys.has(key)) continue

    if (topLevelKeys.has(key)) {
      if (!isEqual(current[key], next)) {
        _set[key] = next
        hasChanges = true
      }
    } else {
      // Never write a resolved entity into jsonb — entity fields store ids.
      if (containsResolvedEntity(next)) {
        // Refusing and CONTINUING meant the caller got back a resolved record with
        // no signal that one of its edits had been dropped — a console.warn no
        // interface surfaces. A refusal the caller cannot observe is not a guard.
        throw new Error(
          `refusing to write a resolved entity object into ${jsonbColumn}.${key}; ` +
            `entity fields store ids, not resolved records. Pass the id, or route the ` +
            `key through a relation field.`
        )
      }
      // Merging into an ABSENT blob would treat it as empty and wipe every
      // other key on write; `undefined` means "not selected", a record whose
      // blob is truly empty carries {} (or null). Slim documents exist —
      // refuse the merge rather than compute a destructive one.
      if (!(jsonbColumn in current)) {
        throw new Error(
          `partitionChanges: writing ${jsonbColumn}.${key} against an item ` +
            `fetched without its ${jsonbColumn} column — merging into an ` +
            `assumed-empty blob would wipe every other key on write. Fetch the ` +
            `item through a document that selects ${jsonbColumn}.`
        )
      }
      // The pre-scan above guarantees no literal jsonbColumn value coexists
      // with merge keys, so seeding from `current` cannot clobber anything.
      const blob = current[jsonbColumn] as Record<string, unknown> | undefined
      if (!isEqual(blob?.[key], next)) {
        jsonbChanges ??= { ...(blob ?? {}) }
        jsonbChanges[key] = next
        hasChanges = true
      }
    }
  }

  if (jsonbChanges) _set[jsonbColumn] = jsonbChanges
  return { _set, hasChanges }
}

const RECORD_TOP_LEVEL = new Set(RECORD_FIXED_KEYS)

// Skip-keys that MEAN something: a caller naming one intends a change, so
// dropping it silently reports a success that never happened. The live case:
// the list archive button called `updateRecord(list, { archived_at })`, the
// only key it passed was skipped, `hasChanges` came back false, the caller's
// `.then()` ran, and the user got "List has been archived" with no mutation
// issued. `planRecordWrite` already refused this key by name; this door did not.
//
// `type` is deliberately NOT here, though `planRecordWrite` does refuse it.
// The two doors have different callers, and that difference is the whole
// reason: planRecordWrite takes a per-field patch, so a `type` in it is always
// deliberate. `computeRecordChanges` is reached by callers that spread a WHOLE
// RECORD — `useRecord.createEditableRecord()` emits `type` and RecordShow.vue
// hands that straight to `update()`, and FinancingForm sends a `type` that is a
// *schema field* (`data.type` on the All Financings schema), not the column.
// Refusing it here broke both: the record edit modal and every financing save.
// So for THIS door `type` is a ride-along, and refusing it would be the bug.
const RECORD_MEANINGFUL_SKIP = {
  archived_at:
    "archived_at is managed by records.archive()/records.unarchive() (a conditional " +
    "update on the server clock that reports already-archived), not by update.",
} as const

// Derived, not restated: every meaningful-skip key must also be skipped, or a
// key whose refusal is later removed would fall through and be WRITTEN —
// `type` into the real column (a silent retype), `archived_at` into
// `data.archived_at` (dead jsonb nothing reads). Deriving makes that structural
// instead of a convention two literals have to keep agreeing on.
const RECORD_SKIP = new Set([
  "id",
  "type",
  "data",
  "created_at",
  "updated_at",
  ...Object.keys(RECORD_MEANINGFUL_SKIP),
])

/** Every column on the `records` table: the fixed fields plus the keys no caller edits
 *  (`id`, `data`, `archived_at`). Derived from the two lists above, so a column
 *  added to either is a column here. */
export const RECORD_COLUMNS: ReadonlySet<string> = new Set([...RECORD_FIXED_KEYS, ...RECORD_SKIP])

/** Columns a filter may target directly: all of them except the `data` jsonb,
 *  which routes to a data predicate. */
export const FILTERABLE_RECORD_COLUMNS: ReadonlySet<string> = new Set(
  [...RECORD_COLUMNS].filter((key) => key !== "data")
)

/** Free-text columns, where substring matching (`contains` / `starts_with`) is
 *  meaningful. `type` and `visibility` hold short enum-like values, so they are
 *  not here even though they are strings in the database. */
export const SUBSTRING_SEARCHABLE_RECORD_COLUMNS: ReadonlySet<string> = new Set(
  FIELDS.filter((f) => f.type === "string").map((f) => f.key)
)

/**
 * Whether a resolved field is a SCHEMA-DECLARED field whose key also names a
 * system column — the collision described at length in `planRecordWrite`.
 *
 * Extracted so the rule has ONE implementation. `planRecordWrite` enforced it
 * and `computeRecordChanges`' door did not, which is how a financing schema's
 * `type` field reached production unwritable: the MCP refused it loudly while
 * the frontend dropped it silently and toasted success. Two doors onto one
 * table disagreeing about which keys are writable is the defect this package
 * exists to remove, so the predicate lives here rather than being restated.
 *
 * Gated on `schema_id != null`: a fixed field named `type`/`created_at` is an
 * ordinary `recordFixedFields` entry (enrichField stamps `schema_id` only for
 * schema-declared fields), and is correctly routed or dropped by
 * RECORD_TOP_LEVEL / RECORD_SKIP membership. Only a DECLARED field colliding
 * with those names is the bug.
 */
export function isSystemColumnCollision(field: AugerField | undefined): boolean {
  if (field?.schema_id == null) return false
  return RECORD_TOP_LEVEL.has(field.key) || RECORD_SKIP.has(field.key)
}

/** The shared refusal text, parameterised by which door caught it. */
export function systemColumnCollisionMessage(door: string, key: string, recordCtx: string): string {
  return (
    `${door}: "${key}" is a schema field on ${recordCtx}, but the key ` +
    `also names a system column — writing it here would either be silently ` +
    `dropped as envelope, or land in the real column while every read of it ` +
    `keeps looking at data.${key}. This collision needs a different key or a ` +
    `dedicated write path; it cannot go through changes.`
  )
}

/**
 * Which keys the record's schemas DECLARE — the same two sources `enrichRecord`
 * merges: the type's default schema, plus the record's own `schema_id`.
 *
 * Resolved here rather than taking a `fields` parameter so the collision guard
 * below needs nothing from its callers. That matters: `useList` calls
 * `computeRecordChanges` directly at three sites, so a guard placed in the
 * frontend's `updateRecord` service — or in the composables — would simply
 * miss them. This function is the one chokepoint every door already goes
 * through.
 *
 * Reads empty when the registries are unwired (tests, pre-boot), which makes
 * the guard inert there. That is a real limitation, not a hidden one: writes
 * cannot meaningfully happen before `auger.load()` anyway, since the field
 * layer resolves nothing. It is NOT the inert-by-accident shape of a
 * try/catch around a throwing enricher — no error is being swallowed.
 */
export function declaredSchemaFieldKeys(record: Record<string, unknown>): Set<string> {
  const keys = new Set<string>()
  const recordType = typeof record.type === "string" ? record.type : null
  const schemaId = typeof record.schema_id === "number" ? record.schema_id : null

  for (const schema of [
    recordType ? (getType(recordType)?.schema ?? null) : null,
    schemaId != null ? getSchema(schemaId) : null,
  ]) {
    for (const f of schema?.fields ?? []) {
      if (typeof f?.key === "string") keys.add(f.key)
    }
  }
  return keys
}

/** Compute the Hasura `_set` mutation payload for record edits. */
export function computeRecordChanges(
  record: Record<string, unknown>,
  changes: Record<string, unknown>
): { _set: Record<string, unknown>; hasChanges: boolean } {
  // A schema-DECLARED field whose key also names a system column cannot go
  // through `changes` at all — see systemColumnCollisionMessage. Checked here,
  // at the shared chokepoint, so every door inherits it: useList's three
  // direct calls and planRecordWrite's tail call (which refuses the same case
  // earlier, with better record context).
  const declared = declaredSchemaFieldKeys(record)
  for (const key of Object.keys(changes)) {
    if (changes[key] === undefined) continue
    if (!declared.has(key)) continue
    if (RECORD_TOP_LEVEL.has(key) || RECORD_SKIP.has(key)) {
      throw new Error(
        systemColumnCollisionMessage("computeRecordChanges", key, `record #${record.id ?? "?"}`)
      )
    }
  }

  for (const [key, why] of Object.entries(RECORD_MEANINGFUL_SKIP)) {
    // Presence, not truthiness: `{ archived_at: null }` is how the old
    // unarchive path spelled its intent, and silently dropping THAT is the
    // half of the bug that reported a successful unarchive. `undefined` still
    // means "not supplied", matching partitionChanges' own rule.
    if (Object.hasOwn(changes, key) && changes[key] !== undefined) {
      throw new Error(
        `computeRecordChanges: record #${record.id ?? "?"} — ${why} ` +
          `Passing it here would be silently dropped.`
      )
    }
  }

  // Half an owner pair names nobody. Checked on the caller's changes, before
  // partitionChanges drops an unchanged value and hides the mistake.
  if (changes.owner_org_id !== undefined && changes.owner_person_id === undefined) {
    ownerWrite({ owner_org_id: changes.owner_org_id })
  }

  const result = partitionChanges(record, changes, {
    topLevelKeys: RECORD_TOP_LEVEL,
    jsonbColumn: "data",
    skipKeys: RECORD_SKIP,
  })
  return { ...result, _set: ownerWrite(result._set) }
}

/**
 * The columns a records `_set` writes for an owner change: the pair
 * (`owner_person_id`, `owner_org_id`).
 *
 * The owner is a staff member, so the org is always Heavybit's. A caller may
 * send `owner_org_id`, but only beside `owner_person_id` and only as
 * Heavybit's; on its own it is refused, since half a pair names nobody.
 *
 * A person must be in the staff directory (the people of the Heavybit Stytch
 * organization, kept by the staff-lists sync): a former staff member or a
 * person nobody can sign in as cannot own a record. Such a person is refused,
 * never dropped.
 */
export function ownerWrite(_set: Record<string, unknown>): Record<string, unknown> {
  const hasPerson = Object.hasOwn(_set, "owner_person_id")
  const hasOrg = Object.hasOwn(_set, "owner_org_id")
  if (!hasPerson) {
    if (hasOrg) {
      throw new Error(
        "owner: owner_org_id is written only together with owner_person_id — " +
          "half a pair names nobody."
      )
    }
    return _set
  }
  const { owner_person_id: value, owner_org_id: org, ...rest } = _set
  const personId =
    value != null && typeof value === "object" ? (value as { id?: unknown }).id : value
  if (personId == null) {
    if (org != null) throw new Error("owner: an org with no owner person names nobody.")
    return { ...rest, owner_person_id: null, owner_org_id: null }
  }
  if (hasOrg && Number(org) !== HEAVYBIT_ORG_RECORD_ID) {
    throw new Error(
      `owner: a record's owner is a Heavybit member, so owner_org_id must be ` +
        `${HEAVYBIT_ORG_RECORD_ID}, not ${String(org)}.`
    )
  }
  const entry = getUserById(Number(personId))
  if (entry == null || entry.former === true) {
    throw new Error(
      `owner: person #${String(personId)} is not in the staff directory, ` +
        `so it cannot own a record.`
    )
  }
  return { ...rest, owner_person_id: Number(personId), owner_org_id: HEAVYBIT_ORG_RECORD_ID }
}

// RELATION CHANGES ============================================================
// The arbiter picks by `isRelationField`: relation fields → computeRelationChanges
// (a reconcile plan applied via the relation service); everything else →
// computeRecordChanges (a Hasura `_set`).

type RelationRow = { id: number | string; schema_key?: string | null; [k: string]: unknown }
type Entity = { id: number | string; [k: string]: unknown }

/** An in-place patch on one owned row: its id and the columns that change. */
export interface RowUpdate {
  id: number
  changes: Record<string, unknown>
}

/** The columns a value relation's row may change in place, per relationship.
 *  A value row IS its value, so these are its content: an edit to one of them
 *  is an update of the same row (same id, created_at, history), never a remove
 *  plus an add. `rows.update` accepts the same columns for these two. */
export const EDITABLE_ROW_COLUMNS: Readonly<Record<"contacts" | "times", readonly string[]>> = {
  contacts: ["value", "label", "is_primary", "position"],
  times: [
    "time_type",
    "title",
    "description",
    "start_at",
    "end_at",
    "timezone",
    "all_day",
    "rrule",
    "capacity",
    "context",
  ],
}

const TIMESTAMP_COLUMNS = new Set(["start_at", "end_at"])
const camel = (column: string) => column.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
const snake = (prop: string) => prop.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)

/** Whether a draft value equals the row's stored one. Timestamps compare as
 *  instants (Hasura returns `+00:00`, a client sends `Z` or an offset); jsonb
 *  compares by content. */
function sameColumnValue(column: string, next: unknown, current: unknown): boolean {
  if (next == null || current == null) return next == null && current == null
  if (TIMESTAMP_COLUMNS.has(column)) {
    const a = Date.parse(String(next))
    const b = Date.parse(String(current))
    if (!Number.isNaN(a) && !Number.isNaN(b)) return a === b
  }
  if (typeof next === "object") return JSON.stringify(next) === JSON.stringify(current)
  return next === current
}

/** The relationship whose rows `field` edits in place, or null for a junction
 *  (its row points at a target; changing the target is a different row) and
 *  for a value relation with nothing editable. */
function editableRelation(field: AugerField): "contacts" | "times" | null {
  if (!isReferenceField(field)) return null
  const def = getFieldType(field.type)
  const relation = isReferenceDef(def) ? def.relationship : undefined
  return relation === "contacts" || relation === "times" ? relation : null
}

/** The columns `entity` changes on `row`. Only what the entity supplies is
 *  compared, so a partial entity (`{ id, startAt }`) patches just that column.
 *  Accepts the camelCase write shape and the snake_case row shape alike. */
function rowPatch(
  relation: "contacts" | "times",
  entity: Entity,
  row: RelationRow
): Record<string, unknown> {
  const changes: Record<string, unknown> = {}
  for (const column of EDITABLE_ROW_COLUMNS[relation]) {
    const prop = camel(column)
    const next = entity[prop] !== undefined ? entity[prop] : entity[column]
    if (next === undefined) continue
    if (!sameColumnValue(column, next, row[column])) changes[column] = next
  }
  return changes
}

/** One update per entity that names a row in `rows` and changes its content.
 *  Columns the entity leaves out, or sends unchanged, are not written. */
function rowUpdates(
  relation: "contacts" | "times",
  entities: Entity[],
  rows: RelationRow[]
): RowUpdate[] {
  const byId = new Map(rows.map((r) => [String(r.id), r]))
  const updates: RowUpdate[] = []
  for (const entity of entities) {
    const row = entity.id == null ? undefined : byId.get(String(entity.id))
    if (!row) continue
    const changes = rowPatch(relation, entity, row)
    if (Object.keys(changes).length) updates.push({ id: Number(row.id), changes })
  }
  return updates
}

/** Whether `row` belongs to `field`'s relation scope. `field.ownsRow` (set for a
 *  field whose `schema_key` alone can't tell it apart from a sibling aggregate
 *  sharing the same unqualified rows — e.g. `socials` within `contacts`) takes
 *  precedence; otherwise membership is the plain `schema_key` match every other
 *  relation field uses. */
const ownsRelationRow = (field: AugerField, row: RelationRow): boolean =>
  field.ownsRow ? field.ownsRow(row) : row.schema_key === field.schema_key

/** A relation diff against a record fetched WITHOUT that relation's rows would
 *  treat every current row as absent — a clear becomes a no-op, a set silently
 *  duplicates rows. `undefined` means "not selected"; a record that truly has
 *  no rows carries `[]`. Refuse the diff rather than compute a wrong one. */
function assertRelationHydrated(
  field: AugerField,
  relation: string | undefined,
  record: Record<string, unknown>
): void {
  if (relation && record?.[relation] === undefined) {
    throw new Error(
      `computeRelationChanges: cannot reconcile "${field.key}" — the record was ` +
        `fetched without its "${relation}" rows, so the diff would treat every ` +
        `current row as absent (a clear becomes a no-op, a set duplicates rows). ` +
        `Fetch the record through a document that selects "${relation}".`
    )
  }
}

/** Reconcile plan for a relation field — NOT a Hasura `_set`. The draft is the
 *  new target ENTITIES; diff them (by `.id`) against the field's current
 *  `schema_key`-scoped rows into the entities to `add` and the row ids to delete.
 *  A junction row's target is its FK column (`reference.column`); a value row's is
 *  its own id.
 *
 *  A SET never edits a row: an entity naming a row it keeps keeps that row as it
 *  is, whatever content the entity carries. A set is usually rebuilt from rows
 *  read earlier, so honouring its content would quietly write a stale copy back
 *  over someone else's edit. Editing a contact or time is the delta's `update`,
 *  which names only the rows and columns that changed (computeRelationOps). */
export function computeRelationChanges(
  field: AugerField,
  item: unknown,
  draft: unknown
): { add: Entity[]; removeRowIds: number[] } {
  const def = getFieldType(field.type)
  const relation = isReferenceDef(def) ? def.relationship : undefined
  const column = isReferenceDef(def) ? (def.reference?.column ?? null) : null
  // `item` may be a listing wrapping the record; the rows live on the record.
  const record = ((item as { record?: unknown })?.record ?? item) as Record<string, unknown>
  assertRelationHydrated(field, relation, record)
  const oldValues = (((relation && record?.[relation]) as RelationRow[]) ?? []).filter((r) =>
    ownsRelationRow(field, r)
  )
  const targetOf = (r: RelationRow) => (column ? r[column] : r.id)

  const oldIds = new Set(oldValues.map((r) => String(targetOf(r))))
  const newValues = (draft == null ? [] : Array.isArray(draft) ? draft : [draft]).filter(
    (v) => v != null
  ) as Entity[]
  const newIds = new Set(newValues.map((e) => String(e.id)))
  const removeRowIds = oldValues
    .filter((r) => !newIds.has(String(targetOf(r))))
    .map((r) => Number(r.id))

  // A value relation has no `reference.column`: the row IS the value, so its
  // identity is the row id and nothing else. Content cannot stand in — the same
  // contact value legitimately appears more than once on a record, in different
  // roles. (Point-in-time production counts that motivated this are in the commit
  // message, not here — they're a snapshot, the invariant isn't.)
  //
  // The arithmetic above therefore treats an id-less value as an ADD and never as a
  // match. Left alone, a caller that supplied only id-less values silently replaced
  // every existing row: ids and created_at reset, label / is_primary / position
  // dropped, and the whole field run through applyRelationOps' non-atomic
  // delete-then-insert for rows nobody was changing.
  //
  // Refuse only the blind case — nothing named, and rows would go. An id that
  // MATCHES a row this field currently owns proves the caller read the field, so a
  // mixed SET that drops a row is deliberate and passes. Checking mere PRESENCE of
  // an "id" key is not enough: a hallucinated, stale, or placeholder id defeats
  // that check completely (`buildObjects` never reads a contact/time/source
  // target's supplied `id` — Postgres mints a fresh one) and reproduces the exact
  // silent wipe this guard exists to prevent. `newValues.length` is load-bearing:
  // `null` yields an empty array and `[].every()` is true, which would otherwise
  // refuse the clear.
  //
  // `isReferenceField(field)` (not `column == null`) scopes this to plain
  // references. A junction is a reference whose inner `reference` is set, so
  // `isReferenceField` is false for it regardless of `column` — which is optional
  // on `ReferenceFieldType`, so a future junction carrying an inner `reference`
  // without a `column` would otherwise misclassify as a value relation.
  // Behaviour-preserving today: the two conditions only differ when the field's
  // def isn't a reference def at all, and then `relation` is undefined, so
  // `removeRowIds` is always empty and the guard can't fire either way.
  if (
    isReferenceField(field) &&
    newValues.length > 0 &&
    removeRowIds.length > 0 &&
    !newValues.some((e) => e.id != null && oldIds.has(String(e.id)))
  ) {
    throw new Error(
      `computeRelationChanges: refusing to SET "${field.key}" — none of the ` +
        `${newValues.length} supplied value(s) names an existing row, so this ` +
        `would delete all ${removeRowIds.length} existing row(s) without the ` +
        `caller ever naming them. A "${field.key}" row IS its value: its identity ` +
        `is the row id, not its content. Supply the "id" of each row you want to ` +
        `KEEP (read the field first: its value IS the rows), send ` +
        `{ add: [...], remove: [...] } for a delta, or send null to clear and ` +
        `then add. To CHANGE a contact or time in place, send ` +
        `{ update: [{ id, ...changed columns }] } — that updates the same row. ` +
        `Sources have no in-place update: remove then add, which mints a new row id.`
    )
  }

  return {
    add: newValues.filter((e) => !oldIds.has(String(e.id))),
    removeRowIds,
  }
}

/** Returns a NEW record (never mutates the input, so the caller keeps the original
 *  to roll back to and a shallow-ref reassignment triggers reactivity) with the
 *  relation field's rows reconciled optimistically: removed rows dropped, one
 *  synthetic row appended per added entity so `field.value`/`field.rawValue`
 *  resolve the new target before the server round trip. Junctions carry the FK
 *  column (`reference.column`) + the hop entity; value relations carry the entity's
 *  own fields. Synthetic ids sort after existing rows; the converging refetch
 *  replaces them with real rows. Returns the input unchanged when the field names
 *  no relation. */
export function applyRelationChanges<R extends Record<string, unknown>>(
  field: AugerField,
  record: R,
  plan: { add: Entity[]; removeRowIds: number[]; update?: RowUpdate[] }
): R {
  const def = getFieldType(field.type)
  const relation = isReferenceDef(def) ? def.relationship : undefined
  if (!relation) return record
  const column = isReferenceDef(def) ? (def.reference?.column ?? null) : null
  const hop = isReferenceDef(def) ? (def.reference?.relationship ?? null) : null

  const remove = new Set(plan.removeRowIds.map(Number))
  const patches = new Map((plan.update ?? []).map((u) => [Number(u.id), u.changes]))
  const kept = ((record[relation] as RelationRow[]) ?? [])
    .filter((r) => !remove.has(Number(r.id)))
    .map((r) => (patches.has(Number(r.id)) ? { ...r, ...patches.get(Number(r.id)) } : r))
  let tempId = kept.reduce((max, r) => Math.max(max, Number(r.id) || 0), 0)
  const added = plan.add.map((entity) => {
    const row: RelationRow = {
      id: ++tempId,
      schema_key: field.schema_key ?? null,
      schema_id: field.schema_id ?? null,
    }
    if (column) row[column] = Number(entity.id)
    if (hop) row[hop] = entity
    // A value row's columns are snake_case, the write's entity camelCase
    // (`startAt`): spell them as the row so the read path resolves them now.
    if (!column && !hop) {
      for (const [key, value] of Object.entries(entity)) {
        if (key !== "id") row[snake(key)] = value
      }
    }
    return row
  })
  return { ...record, [relation]: [...kept, ...added] }
}

// RECORD ENRICHMENT + WRITE PLAN ==============================================

/** A record with its fields resolved: the raw row, the enriched `Field` list
 *  (fixed columns + the schema's fields), and accessors that read a field's value
 *  off the row. An interface renders from this. */
export interface EnrichedRecord {
  raw: RecordItem
  fields: AugerField[]
  field(key: string): AugerField | undefined
  value(key: string): unknown
}

/** Enrich a raw record: resolve its fields from the registry (base type schema
 *  unioned with its own — see below), each stamped with the schema that DECLARED
 *  it, and bind accessors. */
export function enrichRecord(record: RecordItem): EnrichedRecord {
  // Resolve BOTH halves of the union below: the record's explicit `schema_id`, AND
  // its type's default schema. Neither is optional now that a base field is real
  // data (not a fallback) — a registry MISS on either is not a degrade-gracefully
  // case, it is a wiring error, and it used to fail in exactly the direction that
  // causes the bug this package exists to prevent: no schema → `schemaFields = []`
  // → every relation-typed key reads as unknown → straight into `data` as a raw
  // id. A `type` miss is just as fatal as a `schema_id` miss: silently losing the
  // base half of the union would (a) drop every base-declared field from the read
  // side with zero signal, and (b) make the write-side refusal below claim a real,
  // schema-declared key "is not a field on this record" — true only because the
  // registry that would have proven otherwise was never loaded.
  if (record.schema_id != null && getSchema(record.schema_id) == null) {
    throw new Error(
      `enrichRecord: record #${record.id} names schema ${record.schema_id}, which the ` +
        `registry cannot resolve. Either registerSchemas() hasn't run yet, or the ` +
        `resolver returned null for it — the schema may not be loaded, or may be ` +
        `filtered out by this user's row permissions. Continuing would treat every ` +
        `relation field as an unknown key and write ids into data.`
    )
  }
  // `!= null && !== ""` rather than a truthy check, matching the `schema_id`
  // guard's `!= null` style above — same outcome for a string (`""` is treated
  // as "no type," same as `undefined`, deliberately: a record legitimately has
  // no type sometimes, and there's no registry lookup that would tell "no type"
  // apart from "empty-string type"), just consistent about how it's spelled.
  if (record.type != null && record.type !== "" && getType(record.type) == null) {
    throw new Error(
      `enrichRecord: record #${record.id} names type "${record.type}", which the ` +
        `registry cannot resolve. Either registerTypes() hasn't run yet, or "${record.type}" ` +
        `is not a registered type name — continuing would silently drop every field ` +
        `the type's base schema declares.`
    )
  }

  // A record's fields are its type's BASE schema unioned with its own, its own
  // winning on a shared key.
  //
  // Schemas per record type are a base plus specializations: a deal sits on
  // `Investment` or `Commitment` — one field each — while `All Deals` declares the
  // full set that describes a deal. Resolving only the record's own schema would
  // leave nearly every deal with a single field, and the writes that populate the
  // rest looking like keys no field declares. (Point-in-time production counts
  // that motivated this are in the commit message, not here — they're a snapshot,
  // the invariant isn't.)
  //
  // Each field keeps the schema_id of the schema that DECLARED it, not the
  // record's. That is provenance rather than preference, and it is what production
  // already does — see the commit message for the measured split.
  const base: SchemaLike | null =
    record.type != null && record.type !== "" ? (getType(record.type)?.schema ?? null) : null
  const own: SchemaLike | null = record.schema_id != null ? getSchema(record.schema_id) : null

  const declared = new Map<string, AugerField>()
  for (const source of [base, own]) {
    if (source == null) continue
    // A resolved schema with no `id` is a malformed registry entry, not a
    // legitimate "no schema" case (that's `source == null`, handled above) —
    // silently skipping it used to drop every field it declares with no
    // signal, exactly the failure mode the schema_id-miss guard above exists
    // to catch for the OTHER unresolvable-schema path.
    if (source.id == null) {
      throw new Error(
        `enrichRecord: record #${record.id}'s resolved schema has no id — a ` +
          `malformed registry entry. Continuing would silently drop every field ` +
          `it declares.`
      )
    }
    if (!source.fields) continue
    for (const f of source.fields) {
      declared.set(f.key, enrichField({ ...f, schema_id: source.id }))
    }
  }
  const fields = [...recordFixedFields, ...getRelationshipFields(), ...declared.values()]
  const byKey = new Map(fields.map((f) => [f.key, f]))
  return {
    raw: record,
    fields,
    field: (key) => byKey.get(key),
    value: (key) => byKey.get(key)?.value(record),
  }
}

/** The plan for one record write: the Hasura `_set` for columns + `data`, plus a
 *  reconcile op per relation field. This is the write invariant — a relation-typed
 *  key becomes stamped relation rows, never a `data` value. */
export interface RecordWritePlan {
  _set: Record<string, unknown>
  hasChanges: boolean
  relations: Array<{
    field: AugerField
    add: Entity[]
    removeRowIds: number[]
    /** Contacts / times rows whose content changes in place. */
    update: RowUpdate[]
    /** The field's current rows for `add` targets it already holds. Adding
     *  something that already exists writes nothing and returns the row that
     *  already exists — see `computeRelationOps`. */
    existing: RelationRow[]
  }>
}

/** Coerce a relation value to `{ id }` targets — a bare id, an entity, or an array
 *  of either. Lets `update({ introduced_by: 4875 })` route to a link row. */
const toEntities = (value: unknown): Entity[] =>
  (value == null ? [] : Array.isArray(value) ? value : [value])
    .filter((v) => v != null)
    .map((v) => (typeof v === "object" ? (v as Entity) : { id: v as number | string }))

/** A `{ add, remove, update }` delta — the alternative to passing a full set for
 *  a relation field. Detected by those keys, so it never collides with an array
 *  (a set) or a single `{ id }` entity. `update` (contacts and times only) names
 *  rows by id with their new content. */
export const isRelationDelta = (
  v: unknown
): v is { add?: unknown; remove?: unknown; update?: unknown } =>
  !!v &&
  typeof v === "object" &&
  !Array.isArray(v) &&
  ("add" in v || "remove" in v || "update" in v)

/** Reconcile ops for a relation field. An array (or bare id/entity) SETS the field
 *  to exactly those targets; a `{ add, remove }` delta adds/removes without the
 *  caller knowing the current set. `remove` ids match the field's current values
 *  (the linked record id for junctions, the row id for value relations).
 *
 *  Adding something that already exists returns the thing that already exists:
 *  an `add` target the field already holds is not inserted again, and its
 *  current row comes back in `existing` instead, so the caller gets a row for
 *  every target it added either way. A SET reports no `existing` — it names the
 *  whole field, not things to add. */
export function computeRelationOps(
  field: AugerField,
  item: unknown,
  value: unknown
): { add: Entity[]; removeRowIds: number[]; update: RowUpdate[]; existing: RelationRow[] } {
  if (!isRelationDelta(value)) {
    return { ...computeRelationChanges(field, item, toEntities(value)), update: [], existing: [] }
  }

  const def = getFieldType(field.type)
  const relation = isReferenceDef(def) ? def.relationship : undefined
  const column = isReferenceDef(def) ? (def.reference?.column ?? null) : null
  const record = ((item as { record?: unknown })?.record ?? item) as Record<string, unknown>
  assertRelationHydrated(field, relation, record)
  const rows = (((relation && record?.[relation]) as RelationRow[]) ?? []).filter((r) =>
    ownsRelationRow(field, r)
  )
  const targetOf = (r: RelationRow) => (column ? r[column] : r.id)
  const present = new Set(rows.map((r) => String(targetOf(r))))
  // Normalize through toEntities, not String(), so an object-shaped target matches.
  // `String({ id: 5 })` is "[object Object]" and never equals "5", so
  // `remove(key, { id: 5 })` matched nothing and returned success having issued no
  // mutation — while LinkTarget's own type advertises `{ id }` targets.
  const remove = new Set(toEntities(value.remove).map((e) => String(e.id)))
  const add = toEntities(value.add)
  const adding = new Set(add.map((e) => String(e.id)))
  const updates = toEntities(value.update)
  const relationToEdit = editableRelation(field)
  if (updates.length && !relationToEdit) {
    throw new Error(
      `computeRelationOps: "${field.key}" has no in-place update — only contacts ` +
        `and times rows are their own value. Remove the row and add its replacement.`
    )
  }
  // An update must name a row this field owns: an unknown id is a stale or
  // invented one, and silently dropping it would report an edit that never ran.
  const unknown = updates.filter((e) => !rows.some((r) => String(r.id) === String(e.id)))
  if (unknown.length) {
    throw new Error(
      `computeRelationOps: cannot update "${field.key}" row(s) ` +
        `${unknown.map((e) => String(e.id)).join(", ")} — no row of this field has ` +
        `that id. Read the field first: its value IS the rows.`
    )
  }
  return {
    add: add.filter((e) => !present.has(String(e.id))),
    removeRowIds: rows.filter((r) => remove.has(String(targetOf(r)))).map((r) => Number(r.id)),
    update: relationToEdit
      ? rowUpdates(
          relationToEdit,
          updates.filter((e) => !remove.has(String(e.id))),
          rows
        )
      : [],
    // A target both added and removed in one delta is removed, so its row
    // isn't reported as existing.
    existing: rows.filter((r) => {
      const target = String(targetOf(r))
      return adding.has(target) && !remove.has(target)
    }),
  }
}

/**
 * Classify every key of a write against the record's fields: a relation-typed
 * field routes to a reconcile op (stamped rows); every other key partitions into a
 * column or the `data` jsonb. A relationship id therefore cannot reach `data` —
 * there is no branch that puts it there.
 */
export function planRecordWrite(
  fields: AugerField[],
  record: RecordItem,
  changes: Record<string, unknown>
): RecordWritePlan {
  const byKey = new Map(fields.map((f) => [f.key, f]))
  const relations: RecordWritePlan["relations"] = []
  const scalarChanges: Record<string, unknown> = {}
  const recordCtx = `record #${record.id} (type "${record.type}", schema ${record.schema_id ?? "none"})`

  for (const [key, next] of Object.entries(changes)) {
    // `undefined` means "not supplied", for a relation exactly as for a scalar —
    // partitionChanges already skips it on the scalar side. Routing it into
    // computeRelationOps instead produced an empty target list, which is
    // indistinguishable from an explicit empty SET, so an ordinary object spread
    // with an optional key silently deleted every row of that field. Use `null` to
    // clear a relation deliberately.
    if (next === undefined) continue

    // `data` is the container the fields live in, never a field itself, so a
    // patch naming it has the wrong shape and no reading of it is safe. Dropping
    // it as a system container discarded the whole payload and still reported
    // success; spreading it would accept the wrong shape and go on teaching it.
    // Neither leaves the caller anywhere to learn, which is how nested payloads
    // reached production. Refuse the shape and name the right one.
    if (key === "data") {
      throw new Error(
        `planRecordWrite: ${recordCtx} — "data" is the container fields live in, ` +
          `not a field — pass them directly (e.g. { legal_name: … }), not nested ` +
          `under "data".`
      )
    }

    const field = byKey.get(key)

    // A schema-declared field whose TYPE the registry doesn't know cannot be
    // classified. The read path deliberately degrades an unknown type name to
    // `string` (getFieldType's fallback — a mis-render, tolerable), but the
    // write arbiter routing on that fallback would call a relation field a
    // value field and send its ids into `data` — the bug this package exists
    // to prevent, reopened through a typo'd or stale stored type name.
    if (field?.schema_id != null && !hasFieldType(field.type)) {
      throw new Error(
        `planRecordWrite: "${key}" on ${recordCtx} has unregistered field type ` +
          `"${field.type}" — the write cannot be routed. Fix the schema field's ` +
          `type, or register the type in FIELD_TYPES.`
      )
    }

    // A key this record's schema does not declare is not a field of this record.
    // Skills drift — they are prompts, not code, and they invent field names — and
    // an invented one used to land in `data` silently, which is the same shape as
    // the bug this package exists to prevent.
    //
    // Judged against the RESOLVED schema, which is the whole point of schemas: a
    // key a sibling schema of the same type declares is still not a field here,
    // and `enrichRecord` proves it — it builds `fields` from the resolved schema
    // alone, so the read path never surfaces such a key. Writing one produces data
    // no interface can show.
    //
    // The rest of RECORD_SKIP is envelope a caller may pass harmlessly —
    // computeRecordChanges drops them, and dropping them is silent because the
    // caller never meant to set them. `data` is refused above precisely because
    // it fails that test: its contents are exactly what the caller meant to set.
    if (!field && !RECORD_SKIP.has(key)) {
      throw new Error(
        `planRecordWrite: "${key}" is not a field on ${recordCtx} — its schema ` +
          `does not declare it, so writing it into data would be invisible to ` +
          `every reader. Add it to the schema, or correct the key.`
      )
    }

    // A schema CAN declare a field whose key collides with a fixed-column or
    // system-envelope name (e.g. a "type" select field on a financing schema, or
    // an "owner_id" field on some other schema) — `field` resolves, so the
    // refusal above never sees it. Two distinct failure modes follow, and both
    // need refusing:
    //
    //  - Colliding with a RECORD_SKIP name (`data`/`archived_at`/`id`/…): falls
    //    through to computeRecordChanges' `skipKeys` check and is dropped there
    //    silently, because that logic is built for the OTHER case, where nobody
    //    passing `id` or `created_at` means to set them. Here the caller does.
    //  - Colliding with any OTHER fixed column (`owner_id`, `name`, `slug`, …,
    //    the rest of RECORD_FIXED_KEYS): the write itself succeeds — partitions
    //    on the key string alone, with no idea a schema field of the same name
    //    exists, and lands in the real column. But `enrichRecord`'s `byKey` map
    //    lets the schema field's entry win (declared fields are spread last), and
    //    that field's accessors read `record.data[key]` by default — never the
    //    real column. The write and every future read of it now permanently
    //    disagree about where the value lives.
    //
    // Either way, refuse the collision by name rather than let it eat a real
    // write and report success, or make its read side silently wrong forever.
    //
    // Gated on `field.schema_id != null` — a genuine collision, not every field
    // named `type`/`created_at`/`updated_at`. Those three are ALSO ordinary
    // recordFixedFields entries (no schema_id; enrichField only stamps one for
    // schema-declared fields), and are correctly, harmlessly dropped or routed by
    // computeRecordChanges below — that's RECORD_SKIP/RECORD_TOP_LEVEL membership
    // doing its intended job, not the collision this guard exists to catch.
    if (isSystemColumnCollision(field)) {
      throw new Error(systemColumnCollisionMessage("planRecordWrite", key, recordCtx))
    }

    // Two skip-keys are refused rather than silently dropped, because a caller
    // passing them DOES mean to change something — unlike id/created_at/
    // updated_at, which only ride along in spread payloads. (A schema field
    // that COLLIDES with these names was already refused just above.)
    if (key === "archived_at") {
      throw new Error(
        `planRecordWrite: ${recordCtx} — archived_at is managed by ` +
          `records.archive()/records.unarchive() (a conditional update that ` +
          `reports already-archived), not by update. Passing it here would be ` +
          `silently dropped.`
      )
    }
    if (key === "type") {
      throw new Error(
        `planRecordWrite: ${recordCtx} — a record's type cannot be changed ` +
          `through update; it re-anchors the schema union and the slug scope. ` +
          `If retyping is really intended it needs its own migration-shaped path.`
      )
    }

    if (field && isRelationField(field)) {
      relations.push({ field, ...computeRelationOps(field, record, next) })
    } else {
      scalarChanges[key] = next
    }
  }

  const { _set, hasChanges } = computeRecordChanges(record, scalarChanges)
  return { _set, hasChanges, relations }
}
